import { useState, useEffect } from 'react'
import { Navbar, Nav, Container, Button, Dropdown, Badge } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { NotificationBell } from './NotificationBell'
import { PushNotification } from './PushNotification'
import { useTicketCount } from '../contexts/TicketCountContext'

interface LayoutProps {
  children: React.ReactNode
}

export const Layout = ({ children }: LayoutProps) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const { pendingCount: pendingTicketsCount } = useTicketCount()

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      if (window.innerWidth < 768) {
        setSidebarExpanded(false)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    if (isMobile) {
      setSidebarExpanded(false)
    }
  }

  const sidebarWidth = sidebarExpanded ? '280px' : '80px'

  return (
    <div className="layout-container">
      {/* Sidebar */}
      <div 
        className={`sidebar bg-white border-end shadow-sm ${!sidebarExpanded ? 'collapsed' : ''} ${isMobile ? 'mobile-sidebar' : ''} ${isMobile && sidebarExpanded ? 'show' : ''}`}
        style={{ 
          width: isMobile ? '280px' : sidebarWidth, 
          minHeight: '100vh',
          transition: 'all 0.3s ease',
          position: isMobile ? 'fixed' : 'relative',
          zIndex: isMobile ? 1000 : 'auto',
          left: isMobile ? '0' : 'auto',
          overflow: isMobile ? 'hidden' : 'visible'
        }}
      >
        <div className="p-3 d-flex flex-column h-100">
          {/* Logo and Toggle */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            {sidebarExpanded ? (
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded-circle p-2 me-2">
                  <i className="fas fa-headset text-white"></i>
                </div>
                <div>
                  <h5 className="mb-0 text-primary">AACore</h5>
                  <small className="text-muted">Helpdesk</small>
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center w-100">
                <div className="bg-primary rounded-circle p-2">
                  <i className="fas fa-headset text-white"></i>
                </div>
              </div>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="p-2"
              style={{ minWidth: '32px' }}
            >
              <i className={`fas fa-${sidebarExpanded ? 'chevron-left' : 'chevron-right'}`}></i>
            </Button>
          </div>

          {/* Navigation */}
          <Nav className="flex-column flex-grow-1">
            <Nav.Link 
              onClick={() => handleNavigation('/')} 
              className={`nav-link-custom ${isActive('/') ? 'active' : ''}`}
              title="Dashboard"
            >
              <i className={`fas fa-tachometer-alt ${sidebarExpanded ? 'me-3' : ''}`}></i>
              {sidebarExpanded && <span>Dashboard</span>}
            </Nav.Link>
            
            {/* Role-based ticket navigation */}
            {user?.role === 'ADMIN' && (
              <Nav.Link 
                onClick={() => handleNavigation('/tickets')} 
                className={`nav-link-custom ${isActive('/tickets') ? 'active' : ''}`}
                title="All Tickets"
              >
                <i className={`fas fa-ticket-alt ${sidebarExpanded ? 'me-3' : ''}`}></i>
                {sidebarExpanded && (
                  <span>
                    All Tickets{' '}
                    {pendingTicketsCount > 0 && (
                      <span className="text-danger fw-bold">({pendingTicketsCount})</span>
                    )}
                  </span>
                )}
                {!sidebarExpanded && pendingTicketsCount > 0 && (
                  <Badge 
                    bg="danger" 
                    pill 
                    style={{ 
                      position: 'absolute', 
                      top: '5px', 
                      right: '5px', 
                      fontSize: '0.65rem',
                      minWidth: '18px',
                      padding: '2px 5px'
                    }}
                  >
                    {pendingTicketsCount}
                  </Badge>
                )}
              </Nav.Link>
            )}
            
            {user?.role === 'REQUESTER' && (
              <>
                {/* My Tickets */}
                <Nav.Link 
                  onClick={() => handleNavigation('/tickets/my')} 
                  className={`nav-link-custom ${isActive('/tickets/my') ? 'active' : ''}`}
                  title="My Tickets"
                >
                  <i className={`fas fa-folder-open ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && (
                    <span>
                      My Tickets{' '}
                      {pendingTicketsCount > 0 && (
                        <span className="text-danger fw-bold">({pendingTicketsCount})</span>
                      )}
                    </span>
                  )}
                  {!sidebarExpanded && pendingTicketsCount > 0 && (
                    <Badge 
                      bg="danger" 
                      pill 
                      style={{ 
                        position: 'absolute', 
                        top: '5px', 
                        right: '5px', 
                        fontSize: '0.65rem',
                        minWidth: '18px',
                        padding: '2px 5px'
                      }}
                    >
                      {pendingTicketsCount}
                    </Badge>
                  )}
                </Nav.Link>

                {/* Submit Ticket */}
                <Nav.Link 
                  onClick={() => handleNavigation('/tickets/new')} 
                  className={`nav-link-custom ${isActive('/tickets/new') ? 'active' : ''}`}
                  title="Submit a Ticket"
                >
                  <i className={`fas fa-plus-circle ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Submit a Ticket</span>}
                </Nav.Link>

                {/* Divider */}
                {sidebarExpanded && <div className="sidebar-divider my-2"></div>}
                {sidebarExpanded && <div className="sidebar-heading px-3 mb-2">Resources</div>}

                {/* Knowledge Base / Articles */}
                <Nav.Link 
                  onClick={() => handleNavigation('/articles')} 
                  className={`nav-link-custom ${isActive('/articles') ? 'active' : ''}`}
                  title="Knowledge Base"
                >
                  <i className={`fas fa-book ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Knowledge Base</span>}
                </Nav.Link>

                {/* SLA Information */}
                <Nav.Link 
                  onClick={() => handleNavigation('/sla-info')} 
                  className={`nav-link-custom ${isActive('/sla-info') ? 'active' : ''}`}
                  title="SLA Information"
                >
                  <i className={`fas fa-clock ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>SLA Information</span>}
                </Nav.Link>

                {/* Contact Support */}
                <Nav.Link 
                  onClick={() => handleNavigation('/contact-support')} 
                  className={`nav-link-custom ${isActive('/contact-support') ? 'active' : ''}`}
                  title="Contact Support"
                >
                  <i className={`fas fa-headset ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Contact Support</span>}
                </Nav.Link>

                {/* Divider */}
                {sidebarExpanded && <div className="sidebar-divider my-2"></div>}
                {sidebarExpanded && <div className="sidebar-heading px-3 mb-2">My Account</div>}

                {/* Feedback */}
                <Nav.Link 
                  onClick={() => handleNavigation('/feedback')} 
                  className={`nav-link-custom ${isActive('/feedback') ? 'active' : ''}`}
                  title="Feedback"
                >
                  <i className={`fas fa-comment-dots ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Feedback</span>}
                </Nav.Link>

                {/* Reports & Analytics */}
                <Nav.Link 
                  onClick={() => handleNavigation('/my-reports')} 
                  className={`nav-link-custom ${isActive('/my-reports') ? 'active' : ''}`}
                  title="My Reports"
                >
                  <i className={`fas fa-chart-bar ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>My Reports</span>}
                </Nav.Link>

                {/* Account Settings */}
                <Nav.Link 
                  onClick={() => handleNavigation('/account-settings')} 
                  className={`nav-link-custom ${isActive('/account-settings') ? 'active' : ''}`}
                  title="Account Settings"
                >
                  <i className={`fas fa-cog ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Account Settings</span>}
                </Nav.Link>
              </>
            )}
            
            {user?.role === 'AGENT' && (
              <>
                <Nav.Link 
                  onClick={() => handleNavigation('/tickets/agent')} 
                  className={`nav-link-custom ${isActive('/tickets/agent') ? 'active' : ''}`}
                  title="Assigned Tickets"
                >
                  <i className={`fas fa-tasks ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && (
                    <span>
                      Assigned Tickets{' '}
                      {pendingTicketsCount > 0 && (
                        <span className="text-danger fw-bold">({pendingTicketsCount})</span>
                      )}
                    </span>
                  )}
                  {!sidebarExpanded && pendingTicketsCount > 0 && (
                    <Badge 
                      bg="danger" 
                      pill 
                      style={{ 
                        position: 'absolute', 
                        top: '5px', 
                        right: '5px', 
                        fontSize: '0.65rem',
                        minWidth: '18px',
                        padding: '2px 5px'
                      }}
                    >
                      {pendingTicketsCount}
                    </Badge>
                  )}
                </Nav.Link>
                
                <Nav.Link 
                  onClick={() => handleNavigation('/tickets/new')} 
                  className={`nav-link-custom ${isActive('/tickets/new') ? 'active' : ''}`}
                  title="New Ticket"
                >
                  <i className={`fas fa-plus-circle ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>New Ticket</span>}
                </Nav.Link>
              </>
            )}
            
            
            {user?.role === 'ADMIN' && (
              <>
                {/* Divider */}
                {sidebarExpanded && <div className="sidebar-divider my-2"></div>}
                {sidebarExpanded && <div className="sidebar-heading px-3 mb-2">Management</div>}
                
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/users')} 
                  className={`nav-link-custom ${isActive('/admin/users') ? 'active' : ''}`}
                  title="Users"
                >
                  <i className={`fas fa-users ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Users</span>}
                </Nav.Link>

                {/* Divider */}
                {sidebarExpanded && <div className="sidebar-divider my-2"></div>}
                {sidebarExpanded && <div className="sidebar-heading px-3 mb-2">Reports & Analytics</div>}
                
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/reports/tickets')} 
                  className={`nav-link-custom ${isActive('/admin/reports/tickets') ? 'active' : ''}`}
                  title="Ticket Reports"
                >
                  <i className={`fas fa-file-alt ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Ticket Reports</span>}
                </Nav.Link>

                <Nav.Link 
                  onClick={() => handleNavigation('/admin/reports/performance')} 
                  className={`nav-link-custom ${isActive('/admin/reports/performance') ? 'active' : ''}`}
                  title="Performance"
                >
                  <i className={`fas fa-tachometer-alt ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Performance</span>}
                </Nav.Link>

                <Nav.Link 
                  onClick={() => handleNavigation('/admin/reports/agents')} 
                  className={`nav-link-custom ${isActive('/admin/reports/agents') ? 'active' : ''}`}
                  title="Agent Activity"
                >
                  <i className={`fas fa-user-tie ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Agent Activity</span>}
                </Nav.Link>

                {/* Divider */}
                {sidebarExpanded && <div className="sidebar-divider my-2"></div>}
                {sidebarExpanded && <div className="sidebar-heading px-3 mb-2">Integrations</div>}
                
                {/* Settings */}
                <Nav.Link 
                  onClick={() => handleNavigation('/admin')} 
                  className={`nav-link-custom ${isActive('/admin') ? 'active' : ''}`}
                  title="Settings"
                >
                  <i className={`fas fa-cogs ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Settings</span>}
                </Nav.Link>

                {/* User Feedbacks - Admin View */}
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/feedbacks')} 
                  className={`nav-link-custom ${isActive('/admin/feedbacks') ? 'active' : ''}`}
                  title="User Feedbacks"
                >
                  <i className={`fas fa-comments ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>User Feedbacks</span>}
                </Nav.Link>
                
                {/* Articles Management */}
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/articles')} 
                  className={`nav-link-custom ${isActive('/admin/articles') ? 'active' : ''}`}
                  title="Articles"
                >
                  <i className={`fas fa-book ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Articles</span>}
                </Nav.Link>
                
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/webhooks')} 
                  className={`nav-link-custom ${isActive('/admin/webhooks') ? 'active' : ''}`}
                  title="Webhooks"
                >
                  <i className={`fas fa-plug ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Webhooks</span>}
                </Nav.Link>
                
                <Nav.Link 
                  onClick={() => handleNavigation('/admin/email-templates')} 
                  className={`nav-link-custom ${isActive('/admin/email-templates') ? 'active' : ''}`}
                  title="Email Templates"
                >
                  <i className={`fas fa-envelope-open-text ${sidebarExpanded ? 'me-3' : ''}`}></i>
                  {sidebarExpanded && <span>Email Templates</span>}
                </Nav.Link>
                
              </>
            )}
            
            {/* Feedback - Available to agents only (requesters have it in their own menu) */}
            {user?.role === 'AGENT' && (
              <Nav.Link 
                onClick={() => handleNavigation('/feedback')} 
                className={`nav-link-custom ${isActive('/feedback') ? 'active' : ''}`}
                title="Feedback"
              >
                <i className={`fas fa-comment-dots ${sidebarExpanded ? 'me-3' : ''}`}></i>
                {sidebarExpanded && <span>Feedback</span>}
              </Nav.Link>
            )}
          </Nav>

        </div>
      </div>

      {/* Mobile overlay */}
      {isMobile && sidebarExpanded && (
        <div 
          className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
          style={{ zIndex: 999, top: 0, left: 0 }}
          onClick={() => setSidebarExpanded(false)}
        />
      )}

      {/* Main content */}
      <div 
        className="main-content d-flex flex-column"
        style={{ 
          flex: 1,
          minHeight: '100vh',
          transition: 'all 0.3s ease'
        }}
      >
        {/* Top navbar */}
        <Navbar bg="white" className="border-bottom shadow-sm py-2" expand="lg">
          <Container fluid>
            <div className="d-flex align-items-center">
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-3 d-lg-none"
                onClick={() => setSidebarExpanded(!sidebarExpanded)}
              >
                <i className="fas fa-bars"></i>
              </Button>
              <Navbar.Brand className="mb-0 text-truncate">
                {user?.org?.name || 'Helpdesk'}
              </Navbar.Brand>
            </div>
            
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto d-flex align-items-center">
                <div className="me-3">
                  <NotificationBell />
                </div>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <i className="fas fa-user me-2"></i>
                    <span className="d-none d-sm-inline">{user?.name || 'User'}</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {user?.role === 'ADMIN' && (
                      <>
                        <Dropdown.Item onClick={() => handleNavigation('/admin/users')}>
                          <i className="fas fa-users me-2"></i>
                          Users
                        </Dropdown.Item>
                        <Dropdown.Divider />
                      </>
                    )}
                    <Dropdown.Item onClick={() => handleNavigation('/profile')}>
                      <i className="fas fa-user me-2"></i>
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleNavigation('/account-settings')}>
                      <i className="fas fa-cog me-2"></i>
                      Account Settings
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2"></i>
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        {/* Page content */}
        <div className="content-area" style={{ flex: 1 }}>
          {children}
        </div>

        {/* Footer */}
        <footer className="mt-auto py-3 bg-light border-top">
          <Container fluid>
            <div className="text-center text-muted" style={{ fontSize: '0.875rem' }}>
              <i className="fas fa-copyright me-1"></i>
              {new Date().getFullYear()} AACore Helpdesk. All rights reserved.
            </div>
          </Container>
        </footer>
      </div>


      {/* Push Notifications */}
      <PushNotification position="top-end" />
    </div>
  )
}